"use client"

import { Clock, Sparkles, ShoppingCart, Package, Layers } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

const platforms = [
  {
    name: "Shopify",
    description: "Sync products, automate AI visuals, and embed Youzu directly into your Shopify storefront.",
    icon: ShoppingCart,
    color: "from-green-500 to-emerald-600",
    bg: "bg-green-50 dark:bg-green-950/30",
    border: "border-green-200 dark:border-green-800",
  },
  {
    name: "WooCommerce",
    description: "Connect your WooCommerce catalog and deliver AI-powered room visualization to your customers.",
    icon: Package,
    color: "from-purple-500 to-violet-600",
    bg: "bg-purple-50 dark:bg-purple-950/30",
    border: "border-purple-200 dark:border-purple-800",
  },
  {
    name: "commercetools",
    description: "Enterprise-grade composable commerce integration with full product and asset pipeline support.",
    icon: Layers,
    color: "from-blue-500 to-sky-600",
    bg: "bg-blue-50 dark:bg-blue-950/30",
    border: "border-blue-200 dark:border-blue-800",
  },
]

export function PlatformIntegrations() {
  return (
    <div className="space-y-6">
      {/* In-progress banner */}
      <div className="relative overflow-hidden rounded-xl border border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-yellow-950/30 p-5">
        <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-amber-200/40 dark:bg-amber-700/20 blur-2xl" />
        <div className="absolute -left-4 bottom-0 h-20 w-20 rounded-full bg-orange-200/40 dark:bg-orange-700/20 blur-xl" />
        <div className="relative flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/60 border border-amber-200 dark:border-amber-700">
            <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Platform Integrations — Coming Soon</h3>
              <Badge className="bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 text-[10px] px-1.5 py-0 h-4">
                In Progress
              </Badge>
            </div>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/70 leading-relaxed max-w-xl">
              We're actively building native integrations with the most popular e-commerce platforms.
              Early access will be available soon — connect your store and unlock AI-powered product visualization at scale.
            </p>
          </div>
        </div>
      </div>

      {/* Platform cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {platforms.map((platform) => {
          const Icon = platform.icon
          return (
            <Card
              key={platform.name}
              className={`relative overflow-hidden border ${platform.border} ${platform.bg} opacity-80`}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${platform.color} shadow-sm`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{platform.name}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-white/60 dark:bg-white/10">
                      Soon
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{platform.description}</p>

                {/* Shimmer / progress bar */}
                <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${platform.color} animate-shimmer`}
                    style={{ width: "45%" }}
                  />
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Notify CTA */}
      <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-muted-foreground/30 p-4 text-center">
        <Sparkles className="h-4 w-4 text-pink-500" />
        <p className="text-xs text-muted-foreground">
          Integrations are being built — check back soon or contact support to request early access.
        </p>
      </div>
    </div>
  )
}
